/**
 * Public profile screen — `/u/:username`.
 *
 * Fetches `/api/v1/users/:username` (which now includes lifetime stats
 * + member-since date) and renders a read-only profile card. Reachable
 * by tapping a handle anywhere in the app (lobby player list,
 * leaderboard, etc.). For editing one's own profile, see /(app)/settings.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../../../lib/api'
import { goBackOrHome } from '../../../lib/goBack'
import { StatsPanel, type UserStats, type UnrankedStats } from '../../../components/profile/StatsPanel'
import { DiceLuckHistoryPanel } from '../../../components/profile/DiceLuckHistoryPanel'
import type { DiceLuckHistory } from '@warcarp/api-types'

interface PublicProfile {
  id: string
  username: string
  displayName: string
  /** Optional self-reported identity ("who is who"); null when unset. */
  realName: string | null
  stats: UserStats & { losses: number }
  /** Unranked games and ranked + unranked totals. Absent from older servers. */
  unrankedStats?: UnrankedStats
  combinedStats?: UserStats
  diceLuck?: DiceLuckHistory
  memberSince: string
}

function formatMemberSince(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

export default function PublicProfileScreen() {
  const { username } = useLocalSearchParams<{ username: string }>()
  const router = useRouter()

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-public', username],
    queryFn: () => apiGet<PublicProfile>(`/api/v1/users/${encodeURIComponent(username ?? '')}`),
    enabled: !!username,
  })

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#8aa14a" />
      </SafeAreaView>
    )
  }
  if (error || !data) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-3 px-6">
        <Text className="font-stencil text-ink uppercase">Profile not found</Text>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="bg-paper-up border border-olive rounded-md px-4 py-2"
        >
          <Text className="font-stencil text-ink">← Back</Text>
        </TouchableOpacity>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="font-typed text-blood text-base">← Back</Text>
        </TouchableOpacity>
        <Text className="font-stencil text-ink text-lg uppercase">Profile</Text>
      </View>

      <ScrollView className="flex-1" contentContainerStyle={{ padding: 16, gap: 16 }}>
        <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3 items-center">
          <View className="items-center">
            <Text className="font-stencil text-ink text-2xl uppercase">{data.displayName}</Text>
            <Text className="font-typed text-ink-soft text-sm">@{data.username}</Text>
            {!!data.realName && (
              <Text
                className="font-typed text-ink text-sm mt-1"
                accessibilityLabel={`Real name: ${data.realName}`}
              >
                {data.realName}
              </Text>
            )}
          </View>
          <Text className="font-typed text-ink-soft text-xs italic">
            // enlisted {formatMemberSince(data.memberSince)}
          </Text>
        </View>

        <StatsPanel stats={data.stats} unrankedStats={data.unrankedStats} combinedStats={data.combinedStats} />
        {data.diceLuck && (
          <DiceLuckHistoryPanel history={data.diceLuck} />
        )}
      </ScrollView>
    </SafeAreaView>
  )
}
